import React, { useState, useEffect } from "react"
import { useNavigate } from "react-router-dom"
import { useAuth } from "../hooks/useAuth"
import { useIsMobile } from "../hooks/useIsMobile"
import Header from "../components/Header"
import Sidebar from "../components/Sidebar"
import { colors } from "../styles/tokens"
import { navState } from "../crm/services/pageCache"

/**
 * URL do pipeline fica no CRM API (24.199.117.210), não no server de cotações.
 * O blueprint é /api/pipeline, então a URL final é /api/pipeline/dashboard.
 */
const CRM_API_URL = process.env.REACT_APP_CRM_API_URL || 'http://localhost:5000/api/crm'
const PIPELINE_URL = CRM_API_URL.replace('/api/crm', '/api/pipeline')

const fmt = (v) =>
  (v || 0).toLocaleString("pt-BR", { minimumFractionDigits: 2, maximumFractionDigits: 2 })

const PROFILE_LABELS = { solo: "Solo", casal: "Casal", familia: "Família", grupo: "Grupo" }

const PRODUCT_LABELS = {
  package: "Pacote", flight: "Aéreo", hotel: "Hotel", transfer: "Transfer",
  car: "Carro", tour: "Passeio", insurance: "Seguro", bus: "Rodoviário"
}
const PRODUCT_COLORS = {
  package: "#6366f1", flight: "#3b82f6", hotel: "#8b5cf6", transfer: "#f59e0b",
  car: "#10b981", tour: "#06b6d4", insurance: "#ef4444", bus: "#78716c"
}

const monthLabel = (m) => {
  if (!m) return m
  const [y, mo] = m.split("-")
  const names = ["","Jan","Fev","Mar","Abr","Mai","Jun","Jul","Ago","Set","Out","Nov","Dez"]
  return `${names[parseInt(mo)]}/${y}`
}

export default function DashboardPage() {
  const { profile } = useAuth()
  const isMobile = useIsMobile()
  const navigate = useNavigate()
  const sid = profile?.seller_id || null
  const [data, setData] = useState(null)
  const [loading, setLoading] = useState(true)

  // Filtros — inicializam do navState se existir, senão usam defaults
  const _defaultDateFrom = () => {
    const d = new Date()
    return new Date(d.getFullYear(), d.getMonth(), 1).toISOString().split('T')[0]
  }
  const [dateFrom, setDateFrom] = useState(() => navState.get(sid, 'dashboard', 'dateFrom') || _defaultDateFrom())
  const [dateTo, setDateTo] = useState(() => navState.get(sid, 'dashboard', 'dateTo') || new Date().toISOString().split('T')[0])
  const [activePreset, setActivePreset] = useState(() => navState.get(sid, 'dashboard', 'activePreset') || "month")
  const [destination, setDestination] = useState(() => navState.get(sid, 'dashboard', 'destination') || "")
  const [productType, setProductType] = useState(() => navState.get(sid, 'dashboard', 'productType') || "")
  const [travelerProfile, setTravelerProfile] = useState(() => navState.get(sid, 'dashboard', 'travelerProfile') || "")
  const [pipelineStatus, setPipelineStatus] = useState(() => navState.get(sid, 'dashboard', 'pipelineStatus') || "")

  const applyPreset = (key) => {
    const now = new Date()
    const to = now.toISOString().split('T')[0]
    let from
    if (key === 'today') {
      from = now
    } else if (key === 'month') {
      from = new Date(now.getFullYear(), now.getMonth(), 1)
    } else {
      const map = { '7d': 7, '30d': 30, '90d': 90, '365d': 365 }
      from = new Date(now)
      from.setDate(from.getDate() - (map[key] || 30))
    }
    const fromStr = from.toISOString().split('T')[0]
    setDateFrom(fromStr)
    setDateTo(to)
    setActivePreset(key)
    navState.set(sid, 'dashboard', 'dateFrom', fromStr)
    navState.set(sid, 'dashboard', 'dateTo', to)
    navState.set(sid, 'dashboard', 'activePreset', key)
  }

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true)
      try {
        const params = new URLSearchParams()
        if (dateFrom) params.set("date_from", dateFrom)
        if (dateTo) params.set("date_to", dateTo)
        if (destination) params.set("destination", destination)
        if (productType) params.set("product", productType)
        if (travelerProfile) params.set("profile", travelerProfile)
        if (pipelineStatus) params.set("pipeline_status", pipelineStatus)

        const resp = await fetch(`${PIPELINE_URL}/dashboard?${params}`)
        if (resp.ok) setData(await resp.json())
        else setData(null)
      } catch (e) {
        console.error("Dashboard fetch error:", e)
        setData(null)
      } finally {
        setLoading(false)
      }
    }
    fetchData()
  }, [dateFrom, dateTo, destination, productType, travelerProfile, pipelineStatus])

  const kpis = data?.kpis || {}
  const dist = kpis.status_distribution || {}
  const filters = data?.filters_available || {}
  const hasActiveFilter = destination || productType || travelerProfile || pipelineStatus

  const clearFilters = () => {
    setDestination("")
    setProductType("")
    setTravelerProfile("")
    setPipelineStatus("")
    navState.set(sid, 'dashboard', 'destination', "")
    navState.set(sid, 'dashboard', 'productType', "")
    navState.set(sid, 'dashboard', 'travelerProfile', "")
    navState.set(sid, 'dashboard', 'pipelineStatus', "")
  }

  return (
    <div style={{ display: "flex", height: isMobile ? "auto" : "100vh", minHeight: "100vh", backgroundColor: colors.background }}>
      <Sidebar activePage="dashboard" />
      <div style={{ flex: 1, display: "flex", flexDirection: "column", overflow: "hidden" }}>
        <Header subtitle="Dashboard" userName={profile?.name || "Usuário"} />
        <main style={{ flex: 1, overflow: "auto", padding: isMobile ? "1rem" : "2rem", paddingBottom: isMobile ? "80px" : "2rem" }}>

          {/* ── Título + Período ── */}
          <div style={s.topBar}>
            <h2 style={s.title}>Funil de Vendas</h2>
            <div style={{ display: "flex", gap: "0.5rem", alignItems: "center" }}>
              {[
                { key: "today", label: "Hoje" },
                { key: "month", label: "Mês atual" },
                { key: "7d", label: "7 dias" },
                { key: "30d", label: "30 dias" },
                { key: "90d", label: "90 dias" },
                { key: "365d", label: "1 ano" },
              ].map((p) => (
                <button
                  key={p.key}
                  onClick={() => applyPreset(p.key)}
                  style={{
                    ...s.periodBtn,
                    backgroundColor: activePreset === p.key ? colors.primary : "white",
                    color: activePreset === p.key ? "white" : "#6b7280",
                  }}
                >
                  {p.label}
                </button>
              ))}
            </div>
          </div>

          {/* ── Calendário customizado ── */}
          <div style={{ display: "flex", gap: "0.5rem", alignItems: "center", marginBottom: "0.75rem" }}>
            <span style={{ fontSize: "0.8rem", color: "#6b7280", fontWeight: 500 }}>De</span>
            <input
              type="date"
              value={dateFrom}
              onChange={(e) => { setDateFrom(e.target.value); setActivePreset(""); navState.set(sid, 'dashboard', 'dateFrom', e.target.value); navState.set(sid, 'dashboard', 'activePreset', "") }}
              style={s.dateInput}
            />
            <span style={{ fontSize: "0.8rem", color: "#6b7280", fontWeight: 500 }}>Até</span>
            <input
              type="date"
              value={dateTo}
              onChange={(e) => { setDateTo(e.target.value); setActivePreset(""); navState.set(sid, 'dashboard', 'dateTo', e.target.value); navState.set(sid, 'dashboard', 'activePreset', "") }}
              style={s.dateInput}
            />
            <span style={{ fontSize: "0.7rem", color: "#9ca3af", marginLeft: "0.5rem" }}>
              {dateFrom && dateTo ? `${Math.ceil((new Date(dateTo) - new Date(dateFrom)) / 86400000)} dias` : ""}
            </span>
          </div>

          {/* ── Filtros de dimensão ── */}
          <div style={s.filtersRow}>
            <FilterSelect
              label="Destino"
              value={destination}
              onChange={(v) => { setDestination(v); navState.set(sid, 'dashboard', 'destination', v) }}
              options={(filters.destinations || []).map((d) => ({ value: d, label: d }))}
            />
            <FilterSelect
              label="Produto"
              value={productType}
              onChange={(v) => { setProductType(v); navState.set(sid, 'dashboard', 'productType', v) }}
              options={(filters.product_types || []).map((p) => ({
                value: p,
                label: PRODUCT_LABELS[p] || p,
              }))}
            />
            <FilterSelect
              label="Perfil"
              value={travelerProfile}
              onChange={(v) => { setTravelerProfile(v); navState.set(sid, 'dashboard', 'travelerProfile', v) }}
              options={(filters.profiles || []).map((p) => ({
                value: p,
                label: PROFILE_LABELS[p] || p,
              }))}
            />
            <FilterSelect
              label="Status"
              value={pipelineStatus}
              onChange={(v) => { setPipelineStatus(v); navState.set(sid, 'dashboard', 'pipelineStatus', v) }}
              options={[
                { value: "sent", label: "Enviada" },
                { value: "won", label: "Ganho" },
                { value: "lost", label: "Perdido" },
                { value: "paused", label: "Pausada" },
              ]}
            />
            {hasActiveFilter && (
              <button onClick={clearFilters} style={s.clearBtn}>
                Limpar filtros ✕
              </button>
            )}
          </div>

          {loading ? (
            <p style={{ color: colors.text.secondary }}>Carregando...</p>
          ) : !data ? (
            <p style={{ color: colors.text.secondary }}>
              Erro ao carregar dados. Verifique a conexão.
            </p>
          ) : (
            <>
              {/* ── KPIs primários ── */}
              <div style={{ ...s.grid3, gridTemplateColumns: isMobile ? "repeat(2, 1fr)" : "repeat(3, 1fr)" }}>
                <KPICard
                  label="Oportunidades Ativas"
                  value={kpis.active_count || kpis.sent_count || 0}
                  icon="📋"
                  color="#3b82f6"
                />
                <KPICard
                  label="Pipeline Aberto"
                  value={`R$ ${fmt(kpis.pipeline_value)}`}
                  icon="📊"
                  color="#f59e0b"
                  subtitle={`${dist.sent || 0} propostas em aberto`}
                />
                <div style={s.card}>
                  <p style={s.cardLabel}>Distribuição</p>
                  <DistributionBar dist={dist} />
                </div>
              </div>

              {/* ── KPIs secundários ── */}
              <div style={{ ...s.grid3, gridTemplateColumns: isMobile ? "repeat(2, 1fr)" : "repeat(3, 1fr)", marginBottom: "2rem" }}>
                <KPICard
                  label="Receita Fechada"
                  value={kpis.revenue > 0 ? `R$ ${fmt(kpis.revenue)}` : "—"}
                  icon="💰"
                  color="#10b981"
                  subtitle={
                    kpis.revenue > 0
                      ? `${dist.won || 0} propostas ganhas`
                      : "Nenhuma venda no período"
                  }
                  muted={!kpis.revenue}
                />
                <KPICard
                  label="Ticket Médio"
                  value={kpis.avg_ticket != null ? `R$ ${fmt(kpis.avg_ticket)}` : "—"}
                  icon="🎯"
                  color="#8b5cf6"
                  muted={kpis.avg_ticket == null}
                />
                <KPICard
                  label="Taxa de Conversão"
                  value={
                    kpis.conversion_rate != null ? `${kpis.conversion_rate}%` : "—"
                  }
                  icon="📈"
                  color={
                    kpis.conversion_rate >= 30
                      ? "#10b981"
                      : kpis.conversion_rate >= 15
                        ? "#f59e0b"
                        : "#6b7280"
                  }
                  subtitle={
                    kpis.conversion_rate == null
                      ? "Mínimo 5 propostas no funil"
                      : null
                  }
                  muted={kpis.conversion_rate == null}
                />
              </div>

              {/* ── Motivos de Perda ── */}
              {(data.loss_reasons || []).length > 0 && (
                <div style={{ ...s.card, marginBottom: "1rem" }}>
                  <h3 style={s.sectionTitle}>Diagnóstico de Perdas</h3>
                  <LossReasonsChart reasons={data.loss_reasons} totalLost={dist.lost || 0} />
                </div>
              )}

              {/* ── Tabela ── */}
              <div style={s.card}>
                <h3 style={s.sectionTitle}>Oportunidades Ativas</h3>
                {(data.opportunities || data.quotes || []).length === 0 ? (
                  <p style={{ color: "#9ca3af", fontSize: "0.875rem" }}>
                    Nenhuma oportunidade ativa
                  </p>
                ) : (
                  <div style={{ overflowX: isMobile ? "auto" : "visible", WebkitOverflowScrolling: "touch" }}>
                  <table style={s.table}>
                    <thead>
                      <tr style={{ borderBottom: "2px solid #e5e7eb" }}>
                        <th style={s.th}>Cliente</th>
                        <th style={s.th}>Destino</th>
                        <th style={s.th}>Produto</th>
                        <th style={s.th}>Viajantes</th>
                        <th style={s.th}>Opções</th>
                        <th style={{ ...s.th, textAlign: "right" }}>Valor</th>
                        <th style={s.th}>Status</th>
                        <th style={s.th}>Enviada em</th>
                      </tr>
                    </thead>
                    <tbody>
                      {(data.opportunities || data.quotes || []).map((q, i) => {
                        const displayValue = q.display_value != null
                          ? q.display_value
                          : parseFloat(q.total_price || 0) + parseFloat(q.markup || 0)
                        const pax = []
                        if (q.adults) pax.push(`${q.adults}A`)
                        if (q.children) pax.push(`${q.children}C`)
                        if (q.infants) pax.push(`${q.infants}B`)
                        const optCount = q.options_count || 1

                        return (
                          <tr
                            key={i}
                            style={{ borderBottom: "1px solid #f3f4f6" }}
                          >
                            <td style={s.td}>
                              {q.contact_id ? (
                                <span
                                  onClick={() => navigate(`/app/conversas?contact_id=${q.contact_id}`)}
                                  style={{
                                    cursor: 'pointer',
                                    color: '#3b82f6',
                                    fontWeight: 500,
                                    textDecoration: 'none',
                                  }}
                                  onMouseEnter={e => e.target.style.textDecoration = 'underline'}
                                  onMouseLeave={e => e.target.style.textDecoration = 'none'}
                                >
                                  {q.client_name}
                                </span>
                              ) : (
                                q.client_name
                              )}
                            </td>
                            <td style={s.td}>{q.destination}</td>
                            <td style={s.td}>
                              {q.product_category
                                ? <span style={{
                                    padding: "2px 6px",
                                    borderRadius: "4px",
                                    fontSize: "0.65rem",
                                    fontWeight: 600,
                                    color: "white",
                                    backgroundColor: PRODUCT_COLORS[q.product_category] || "#6b7280",
                                  }}>
                                    {PRODUCT_LABELS[q.product_category] || q.product_category}
                                  </span>
                                : "—"}
                            </td>
                            <td style={s.td}>{pax.join(" ") || "—"}</td>
                            <td style={s.td}>
                              <span style={{
                                padding: "2px 8px",
                                borderRadius: "10px",
                                fontSize: "0.7rem",
                                fontWeight: 600,
                                background: optCount > 1 ? "#eff6ff" : "#f3f4f6",
                                color: optCount > 1 ? "#3b82f6" : "#6b7280",
                              }}>
                                {optCount}
                              </span>
                            </td>
                            <td
                              style={{
                                ...s.td,
                                textAlign: "right",
                                fontWeight: 600,
                              }}
                            >
                              R$ {fmt(displayValue)}
                            </td>
                            <td style={s.td}>
                              <StatusBadge
                                status={q.pipeline_status || "sent"}
                                reason={q.closed_reason}
                              />
                            </td>
                            <td style={{ ...s.td, color: "#9ca3af" }}>
                              {q.sent_at
                                ? new Date(q.sent_at).toLocaleDateString(
                                    "pt-BR"
                                  )
                                : "—"}
                            </td>
                          </tr>
                        )
                      })}
                    </tbody>
                  </table>
                  </div>
                )}
              </div>
            </>
          )}
        </main>
      </div>
    </div>
  )
}


// ─── Sub-componentes ──────────────────────────────────────

function KPICard({ label, value, icon, color, subtitle, muted }) {
  return (
    <div style={{ ...s.card, opacity: muted ? 0.55 : 1 }}>
      <div style={{ display: "flex", alignItems: "center", gap: "0.5rem", marginBottom: "0.5rem" }}>
        <span style={{ fontSize: "1.1rem" }}>{icon}</span>
        <p style={s.cardLabel}>{label}</p>
      </div>
      <p style={{ margin: 0, fontSize: "1.75rem", fontWeight: 700, color }}>
        {value}
      </p>
      {subtitle && (
        <p style={{ margin: "0.25rem 0 0", fontSize: "0.72rem", color: "#9ca3af" }}>
          {subtitle}
        </p>
      )}
    </div>
  )
}

function DistributionBar({ dist }) {
  const total = (dist.sent || 0) + (dist.won || 0) + (dist.lost || 0)
  if (total === 0)
    return (
      <p style={{ color: "#9ca3af", fontSize: "0.8rem", margin: 0 }}>
        Sem dados
      </p>
    )

  const stages = [
    { key: "sent", label: "Enviada", color: "#3b82f6", count: dist.sent || 0 },
    { key: "won", label: "Ganho", color: "#10b981", count: dist.won || 0 },
    { key: "lost", label: "Perdido", color: "#ef4444", count: dist.lost || 0 },
    { key: "paused", label: "Pausada", color: "#eab308", count: dist.paused || 0 },
  ]

  return (
    <div>
      <div style={s.barContainer}>
        {stages
          .filter((st) => st.count > 0)
          .map((st) => (
            <div
              key={st.key}
              style={{
                width: `${(st.count / total) * 100}%`,
                backgroundColor: st.color,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                color: "white",
                fontSize: "0.7rem",
                fontWeight: 600,
                minWidth: "28px",
              }}
            >
              {st.count}
            </div>
          ))}
      </div>
      <div style={{ display: "flex", gap: "1rem" }}>
        {stages.map((st) => (
          <div
            key={st.key}
            style={{ display: "flex", alignItems: "center", gap: "5px" }}
          >
            <div
              style={{
                width: "8px",
                height: "8px",
                borderRadius: "50%",
                backgroundColor: st.color,
              }}
            />
            <span style={{ fontSize: "0.7rem", color: "#6b7280" }}>
              {st.label}: {st.count}
            </span>
          </div>
        ))}
      </div>
    </div>
  )
}

function FilterSelect({ label, value, onChange, options }) {
  return (
    <select
      value={value}
      onChange={(e) => onChange(e.target.value)}
      style={{
        padding: "0.4rem 0.8rem",
        fontSize: "0.8rem",
        borderRadius: "8px",
        border: "1px solid #e5e7eb",
        backgroundColor: value ? "#f0fdf4" : "white",
        color: value ? "#065f46" : "#6b7280",
        cursor: "pointer",
        fontWeight: 500,
        minWidth: "120px",
      }}
    >
      <option value="">{label}</option>
      {options.map((o) => (
        <option key={o.value} value={o.value}>
          {o.label}
        </option>
      ))}
    </select>
  )
}

function LossReasonsChart({ reasons, totalLost }) {
  const COLORS = {
    budget: "#f59e0b",
    competitor: "#ef4444",
    gave_up: "#6b7280",
    no_response: "#8b5cf6",
    disintermediation: "#3b82f6",
    misaligned: "#ec4899",
  }

  return (
    <div>
      {/* Barra horizontal empilhada */}
      <div style={s.barContainer}>
        {reasons.map((r) => (
          <div
            key={r.key}
            style={{
              width: `${(r.count / totalLost) * 100}%`,
              backgroundColor: COLORS[r.key] || "#9ca3af",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              color: "white",
              fontSize: "0.7rem",
              fontWeight: 600,
              minWidth: "24px",
            }}
            title={`${r.label}: ${r.count}`}
          >
            {r.count}
          </div>
        ))}
      </div>

      {/* Legenda detalhada */}
      <div style={{ display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: "0.5rem 1rem", marginTop: "0.25rem" }}>
        {reasons.map((r) => {
          const pct = totalLost > 0 ? Math.round((r.count / totalLost) * 100) : 0
          return (
            <div key={r.key} style={{ display: "flex", alignItems: "center", gap: "6px" }}>
              <div
                style={{
                  width: "8px",
                  height: "8px",
                  borderRadius: "50%",
                  backgroundColor: COLORS[r.key] || "#9ca3af",
                  flexShrink: 0,
                }}
              />
              <span style={{ fontSize: "0.7rem", color: "#6b7280" }}>
                {r.emoji} {r.label}: {r.count} ({pct}%)
              </span>
            </div>
          )
        })}
      </div>
    </div>
  )
}

function StatusBadge({ status, reason }) {
  const config = {
    sent: { bg: "#dbeafe", color: "#1e40af", label: "📲 Enviada" },
    won: { bg: "#d1fae5", color: "#065f46", label: "✓ Ganho" },
    lost: { bg: "#fee2e2", color: "#991b1b", label: "✗ Perdido" },
  }
  const c = config[status] || config.sent
  return (
    <span
      title={reason || ""}
      style={{
        padding: "2px 8px",
        borderRadius: "10px",
        fontSize: "0.7rem",
        fontWeight: 600,
        backgroundColor: c.bg,
        color: c.color,
        cursor: reason ? "help" : "default",
      }}
    >
      {c.label}
    </span>
  )
}


// ─── Styles ──────────────────────────────────────────────

const s = {
  topBar: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    marginBottom: "1rem",
  },
  title: {
    margin: 0,
    fontSize: "1.5rem",
    fontWeight: 700,
    color: colors.text.primary,
  },
  periodBtn: {
    padding: "0.4rem 0.9rem",
    border: "1px solid #e5e7eb",
    borderRadius: "8px",
    cursor: "pointer",
    fontSize: "0.8rem",
    fontWeight: 600,
    transition: "all 0.15s",
  },
  dateInput: {
    padding: "0.35rem 0.6rem",
    border: "1px solid #e5e7eb",
    borderRadius: "8px",
    fontSize: "0.8rem",
    color: "#374151",
    fontWeight: 500,
    cursor: "pointer",
  },
  filtersRow: {
    display: "flex",
    gap: "0.75rem",
    alignItems: "center",
    marginBottom: "1.5rem",
    flexWrap: "wrap",
  },
  clearBtn: {
    padding: "0.4rem 0.8rem",
    fontSize: "0.75rem",
    fontWeight: 600,
    border: "1px solid #d1d5db",
    borderRadius: "6px",
    cursor: "pointer",
    backgroundColor: "white",
    color: "#6b7280",
  },
  grid3: {
    display: "grid",
    gridTemplateColumns: "repeat(3, 1fr)",
    gap: "1rem",
    marginBottom: "1rem",
  },
  card: {
    backgroundColor: "white",
    borderRadius: "12px",
    border: "1px solid #e5e7eb",
    padding: "1.25rem",
  },
  cardLabel: {
    margin: "0 0 0.5rem",
    fontSize: "0.8rem",
    color: "#6b7280",
    fontWeight: 500,
  },
  sectionTitle: {
    margin: "0 0 1rem",
    fontSize: "1rem",
    fontWeight: 600,
    color: colors.text.primary,
  },
  barContainer: {
    display: "flex",
    height: "32px",
    borderRadius: "8px",
    overflow: "hidden",
    marginBottom: "0.5rem",
  },
  table: {
    width: "100%",
    borderCollapse: "collapse",
    fontSize: "0.8rem",
  },
  th: {
    textAlign: "left",
    padding: "0.5rem 0.75rem",
    color: "#6b7280",
    fontWeight: 600,
    fontSize: "0.75rem",
  },
  td: {
    padding: "0.5rem 0.75rem",
    color: "#1f2937",
  },
}
